import isLocalStorageAvailable from '../services/mini/isLocalStorageAvailable';
import { FONT_MODE_STOTAGE_KEY, CODE_THEME_STOTAGE_KEY } from '../const/storageKeys';
import { DEFAULT_FONT } from '../const/fonts';
import { DEFAULT_CODE_THEME } from '../const/codeThemes';
import { IInvitedFriend } from '../models/IInvitedFriend';
import { IProject } from '../models/IProject';
import { IAlanBtnPopup } from '../models/IAlanBtnPopup';
import { ICodeIdentifier, IImportIdentifier } from '../models/ICodeIdentifier';
import { ISyntaxError } from '../models/ISyntaxError';
import { IProjectApiData } from '../models/IProjectApiData';
import { IProjectApiCallInfo } from '../models/IProjectApiCallInfo';
import { IIntent } from '../models/IIntent';
import { ITransformInScript } from '../models/ITransformInScript';
import { ICorpusData } from '../models/ICorpusData';
import { ICorpusDocument } from '../models/ICorpusDocument';

const initialState = {
    projects: [],
    projectsStats: {},
    projectsLocationStats: {},
    projectsAnalytics: {},
    projectsAnalyticsSettings: {},
    projectsAnalyticIntents: {},
    projectsAnalyticPatterns: {},
    projectsAnalyticPatternFlows: {},
    projectsUsersAnalytics: {},
    hasProjectsData: false,
    connectionDetails: {},

    user: {
        email: '',
        githubLogin: '',
        balance: 0,
        billingStatus: null,
        freeInteractions: 0,
        tier: null,
        allowedToWork: false,
        hasAssignedCard: false,
        logInInProcess: false,
        hasProfileData: false,
        authorizationInProcess: false,
        accountVerificationIsInInProcess: false,
        hasPaymentInfo: false,
        payments: null,
        verificationEmailInfoData: {},
        plans: [],
        planId: null,
        isEnterprise: false,
        cancelPlanTs: 0,
        currentPlan: {},
        paidUntilTs: null,
        capabilities: {},
        cards: null,
        campaignName: null,
        numOfStars: 0,
        invitedFriends: null,
        earnedFreeInteractionsPerMonth: null,
    } as IUserStore,

    clientCodeRunner: {
        paramsHistory: {}
    } as IClientCodeRunnerStore,

    currentProject: {
        scriptsGraphCache: {},
        scripts: [],
        savedOptions: [],
        reloadProjectTs: null,
        hasScriptsData: false,
        scriptsLoading: true,
        openedLargeFiles: {},
        conversations: null,
        addingScriptsIsInprocess: false,
        isProjectConnected: false,
        isTestCaseRefreshing: false,
        isTestCaseRefreshingAutoScrollAllowed: true,
        isImportingOfTestCasesInProgress: false,
        isImportingScriptsInProgress: false,
        scriptsAreActual: null,
        scriptsHash: null,
        exportingLogs: false,
        downloadingScripts: false,
        visualStatePanelOpened: false,
        visualStates: [],
        visualState: {},
        selectedVisualStateTs: null,
        authDataPanelOpened: false,
        authData: null,
        activePatterns: {},
        activePatternsUpdateTs: null,
        tryMessage: null,
        connectDetails: {},
        importConflictScripts: [],
        cohorts: [],
        wakeWords: [],
        defaultWakeWord: null,
        defaultWakeWordOptions: null,
        hasCohortsData: false,
        projectDecriptionError: false,
    },

    diffMode: {
        scripts: [],
        fromTagId: null,
        toTagId: null,
        diffModeScriptsUpdateTs: null,
        selectedScript: {},
        isLoading: true
    },

    testMode: {
        dialogTree: null,
        dialogs: null,
        actualDialogId: null,
        activeDialogIdForQrCode: null,
        activeDialogConnectedByQrCode: null,
        isDialogsRefreshingInProcess: false,
        expectedResponsesAreShownGlobally: false,
        commandsInTestModeAreShownGlobally: true,
        codeLinksAreShownGlobally: true,
        dialogItemsWithOpenedExpectedResponses: {},
        copiedExpectedResponses: null,
        copiedNodes: null,
        refreshingDialogs: {},
        conversationsInfo: {},
        refreshDialogTreeCounter: {
            all: 1,
            count: 1
        },
        dialogsInfo: {},
    },

    microphone: {
        isActive: false
    },

    organization: {
        isUpdating: false
    } as IOrganizationStore,

    corpus: {
        corpusId: null,
        title: null,
        file: null,
        document: null,
        searchInProcess: false
    } as ICorpusStore,

    gitHubIntegration: {
        hasGithubToken: null, // we have ghToken
        hasGithubAccess: null,// we can retrieve data with this token
        repoUrl: '', // we have saved github repo url to make requests
        gitUserProject: null,
        githubLogin: '',
        githubDeployId: '',
        isPulling: false,
        isPushing: false,
        isRequestProcessing: false,
        hasChangesToPull: false,
        hasChangesToPush: false,
        repositories: null,
        organizations: null,
        branches: null,
        remoteFiles: null,
        repoPermissions: {}
    } as IGitHubIntegrationStore,

    editor: isLocalStorageAvailable() ?
        {
            fontSize: localStorage.getItem(FONT_MODE_STOTAGE_KEY) || DEFAULT_FONT,
            codeTheme: localStorage.getItem(CODE_THEME_STOTAGE_KEY) || DEFAULT_CODE_THEME,
        } :
        {
            fontSize: DEFAULT_FONT,
            codeTheme: DEFAULT_CODE_THEME,
        },

    config: {
        voices: {},
        acousticModels: [],
        sluVersions: [],
        features: null
    },
    messages: [],
    predefinedScripts: null,
    isProjectResetting: false,
    isProjectCreating: false,
    isProjectDeleting: false,
    isProjectOptionsSaving: false,
    isMobileWarningShown: false,
};

export interface IStore {
    user: IUserStore
    projectsInfo: IProjectsInfo
    scriptEditor: ISciptEditorStore
    codeMetaData: ICodeMetaDataStore
    clientCodeRunner: IClientCodeRunnerStore
    gitHubIntegration: IGitHubIntegrationStore
    organization: IOrganizationStore
    corpus: ICorpusStore
    // add type for config
    config: any
    testMode: any
}

export interface IOrganizationStore {
    isUpdating: boolean;
}

export interface ICorpusStore {
    corpusId: string;
    searchInProcess: boolean;
    title: string;
    file: string;
    corpusSearchQuery: string;
    corpusSearchQustion: string;
    corpusMaxTokens: number;
    searchedDocuments: ICorpusDocument[],
    selectedDocument: ICorpusDocument,
    selectedDocumentInSearch: ICorpusDocument,
    document: {
        content: string[],
        meta: {
            mime_type: string
        }
    }
}

export interface IGitHubIntegrationStore {
    hasGithubToken: boolean | null,
    hasGithubAccess: boolean | null,
    repoUrl: string,
    gitUserProject: null | string,
    githubLogin: string,
    githubDeployId: string,
    isPulling: boolean,
    isPushing: boolean,
    isRequestProcessing: boolean,
    hasChangesToPull: boolean,
    hasChangesToPush: boolean,
    repositories: any, //todo: fix any
    organizations: any,
    branches: any,
    remoteFiles: any,
    repoPermissions: any
}

export interface IClientCodeRunnerStore {
    paramsHistory: { [key: string]: IProjectApiCallInfo[] }
}
export interface ICodeMetaDataStore {
    isParsingInProcess: boolean,
    imports: Map<string, IImportIdentifier>,
    methodsMap: { [key: string]: ICodeIdentifier; },
    membersMap: { [key: string]: ICodeIdentifier[]; },
    brackets: any, //todo: remove any and describe type/interface
    strings: any, //todo: remove any and describe type/interface
    playStrings: any, //todo: remove any and describe type/interface
    declarationIndex: any, //todo: remove any and describe type/interface
    patternStrings: any, //todo: remove any and describe type/interface
    invalidBrackets: any[], //todo: remove any and describe type/interface
    alanBtnPopups: { [key: string]: IAlanBtnPopup },
    projectApis: { [key: string]: IProjectApiData },
    corpuses: { [key: string]: ICorpusData },
    intents: { [key: string]: IIntent },
    transforms: { [key: string]: ITransformInScript },
    bracketsInStrings: object,
    syntaxErrors: ISyntaxError[],
    hasUnsavedChanges: boolean,
    codeString: string,
    parsedScriptsInfo: {
        scriptsCount: number,
        parsedScriptsCount: number,
    }
}
export interface ISciptEditorStore {
    highlightedLine: number,
    highlightedLineType: string,
    highlightedLineOffset: number,
    highlightedDefinitionName: string,
    fontSize: string,
    codeTheme: string,
    searchPanels: {
        [key: string]: {
            showSearch: boolean,
            showReplace: boolean,
            searchTerm: string,
            replaceTerm: string,
            totalSearchResults: number,
            currentSearchItem: number,
            lastOpen: number,
            lastSearchUpdate: number,
            statsScriptsSearch: {
                count: number,
                isLargeFile: boolean
            },

        }
    }
}

export interface IProjectsInfo {
    currentProject: IProject
    isProjectOptionsSaving: boolean
    hasProjectsData: boolean
    isProjectCreating: boolean
    isProjectDeleting: boolean
    isImportingScriptsInProgress: boolean
    projects: IProject[]
    projectsStats: any // todo: describe type
    connectionDetails: any
}

export interface ICapabilities {
    cohortsAvailable: boolean
    customButtonLogo: boolean
    customWakeWordAvailable: boolean
    customWakeWord3Available: boolean
    videoToolAvailable: boolean
    voicesSettingsAvailable: boolean
    createOrgAvailable: boolean
    transformsAvailable: boolean

}

export interface IUserStore {
    email: string,
    githubLogin: string,
    balance: number,
    billingStatus: string,
    freeInteractions: number,
    allowedToWork: boolean,
    hasAssignedCard: boolean,
    tier: string,
    logInInProcess: boolean,
    hasProfileData: boolean,
    authorizationInProcess: boolean,
    accountVerificationIsInInProcess: boolean,
    hasPaymentInfo: boolean,
    payments: null,
    verificationEmailInfoData: object,
    plans: [],
    planId: string,
    isEnterprise: boolean,
    cancelPlanTs: number,
    currentPlan: object,
    paidUntilTs: number,
    capabilities: ICapabilities,
    cards: null,
    campaignName: string,
    numOfStars: number,
    invitedFriends: IInvitedFriend[],
    earnedFreeInteractionsPerMonth: number,
    org: IOrganization | null,
    memberOf: string,
}

export interface IOrganizationMember {
    email: string;
    active: boolean;
    approvedTs: number;
    createTs: number;
}

export interface IOrganization {
    name: string;
    members: IOrganizationMember[]
}

export default initialState;
